"use client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { commonService } from "@/services/commonService";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import { useLocale } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Spinner from "../ui/Spinner";

const NewsletterForm = () => {
  const t = useTranslationDetector();
  const locale = useLocale();
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Zod validation schema
  const newsletterSchema = z.object({
    email: z
      .string()
      .min(1, t("footer.validationsemailrequired"))
      .email(t("footer.validationsemailinvalid"))
      .max(255, t("footer.validationsemailmax")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const response = await commonService.emailSubscribe({
        UserId: session?.UserId,
        LanguageCode: locale,
        Email: data.email,
        NewsletterType: 0,
      });
      if (!response?.Message) {
        setSubmitSuccess(true);
        reset();
      }
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      setSubmitError(
        t(`${error.response.data.Type}.${error.response.data.Name}`),
      );
      setTimeout(() => setSubmitSuccess(false), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={handleSubmit(onSubmit)} className="flex w-full">
        <div className="border-primary-weak inline-flex w-full justify-between overflow-hidden rounded-lg border border-r-0 md:max-w-[380px]">
          <input
            {...register("email")}
            placeholder={t("footer.newsletterplaceholder")}
            className={clsx(
              "w-full border-0 px-4 py-3 ring-0 focus:outline-none",
              errors.email && "border-red-500",
            )}
            type="email"
            disabled={isSubmitting}
            autoComplete="off"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 flex h-12 w-14 cursor-pointer items-center justify-center rounded-r-lg text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <Spinner className="h-4 w-4 animate-spin text-white" />
            ) : (
              <FontAwesomeIcon
                icon={byPrefixAndName.fal["angle-right"]}
                className="text-2xl"
              />
            )}
          </button>
        </div>
      </form>

      {/* Error message */}
      {errors.email && (
        <p className="text-danger flex items-center gap-1 pl-4 text-xs">
          <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
          <span>{errors.email.message}</span>
        </p>
      )}

      {/* Success message */}
      {submitSuccess && (
        <p className="flex items-center gap-1 pl-4 text-xs text-green-500">
          <FontAwesomeIcon icon={byPrefixAndName.far["circle-check"]} />
          <span>{t("footer.newslettersuccess")}</span>
        </p>
      )}
      {submitError && (
        <p className="text-danger flex items-center gap-1 pl-4 text-xs">
          <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
          <span>{submitError}</span>
        </p>
      )}
    </div>
  );
};

export default NewsletterForm;
